import { Controller } from "@hotwired/stimulus";
import { httpRequest } from "../http";

const POLL_INTERVAL_MS = 7000;

export default class extends Controller {
  static values = {
    generateUrl: String,
    statusUrl: String,
    errorMessage: {
      type: String,
      default: "Export failed. Please try again.",
    },
    showTest: { type: Boolean, default: true },
  };

  static targets = ["button", "idle", "busy", "error"];

  connect() {
    this.pollTimer = null;
    this.readyReportUrl = null;
    this.generating = false;
    this.checkStatusOnLoad();
  }

  disconnect() {
    this.stopPolling();
  }

  export(event) {
    event.preventDefault();

    if (this.readyReportUrl) {
      window.location.href = this.readyReportUrl;
      return;
    }

    if (this.generating) return;

    this.setGenerating(true);
    this.clearError();

    this.requestJson(this.generateUrlValue, { method: "POST" })
      .then((data) => this.handleResponse(data, { autoDownload: true }))
      .catch(() => {
        this.stopPolling();
        this.setGenerating(false);
        this.showError(this.errorMessageValue);
      });
  }

  // The PDF chart follows the page's Show Test checkbox, and each state is a separate report.
  setShowTest({ detail: { showTest } }) {
    this.showTestValue = showTest;
    this.stopPolling();
    this.setGenerating(false);
    this.clearError();
    this.readyReportUrl = null;
    this.checkStatusOnLoad();
  }

  // ── Private ──────────────────────────────────────────────────────────

  // A response for the other Show Test state resolves to {} so it can't mark the wrong report ready.
  requestJson(url, options) {
    const showTest = this.showTestValue;
    const target = new URL(url, window.location.origin);
    if (!showTest) target.searchParams.set("show_test", "0");

    return httpRequest(target, options)
      .then((r) => r.json())
      .then((data) => (showTest === this.showTestValue ? data : {}));
  }

  checkStatusOnLoad() {
    if (!this.statusUrlValue) return;

    this.requestJson(this.statusUrlValue, { method: "GET", cache: "no-store" })
      .then((data) => {
        // Ignore stale errored rows from a previous session; errors are shown only after the user exports.
        if (data.status === "errored") return;

        this.handleResponse(data, { autoDownload: false });
      })
      .catch(() => {});
  }

  handleResponse(data, { autoDownload = true } = {}) {
    if (data.status === "completed" && data.report_url) {
      this.stopPolling();
      this.setGenerating(false);
      this.readyReportUrl = data.report_url;
      this.clearError();

      if (autoDownload) {
        window.location.href = this.readyReportUrl;
      }
      return;
    }

    if (["generating", "waiting_for_calculation"].includes(data.status)) {
      this.setGenerating(true);
      this.clearError();
      if (data.status_url) this.statusUrlValue = data.status_url;
      this.startPolling();
      return;
    }

    if (data.status === "errored") {
      this.stopPolling();
      this.setGenerating(false);
      this.readyReportUrl = null;
      this.showError(
        data.message || this.errorMessageValue,
      );
    }
  }

  startPolling() {
    if (this.pollTimer) return;

    this.pollTimer = setInterval(() => {
      this.requestJson(this.statusUrlValue, { method: "GET", cache: "no-store" })
        .then((data) => this.handleResponse(data))
        .catch(() => {
          this.stopPolling();
          this.setGenerating(false);
        });
    }, POLL_INTERVAL_MS);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  setGenerating(value) {
    this.generating = value;

    if (this.hasButtonTarget) {
      this.buttonTarget.disabled = value;
      this.buttonTarget.classList.toggle("cursor-not-allowed", value);
      this.buttonTarget.classList.toggle("opacity-50", value);
    }

    if (this.hasIdleTarget) this.idleTarget.classList.toggle("hidden", value);
    if (this.hasBusyTarget) this.busyTarget.classList.toggle("hidden", !value);
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
      this.errorTarget.classList.add("hidden");
    }
  }
}
